import {
  Box,
  Button,
  Link,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Download } from 'lucide-react';
import { useLanguage, useT } from '../../i18n';
import { useStoreLinks } from '../../lib/siteConfig';
import {
  BADGES,
  badgeLayout,
  hasAnyBadge,
  storeChoice,
  storeDevice,
  type BadgeArt,
  type StoreDevice,
  type StoreLinks,
} from '../../lib/storeLinks';

// The official "Download on the App Store" and "Get it on Google Play"
// badges (D344–D345), served from Twinden (client/public/badges), each shown
// only while its link is set. Both brands' rules: unmodified artwork, the
// same height (at least 40px), clear space of a quarter of the height, the
// App Store first.

function Badge({
  href,
  art,
  label,
  height,
}: {
  href: string;
  art: BadgeArt;
  label: string;
  height: number;
}) {
  const layout = badgeLayout(art, height);
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      display="block"
      w={`${layout.frameWidth}px`}
      h={`${height}px`}
      overflow="hidden"
      borderRadius={`${Math.round(height / 5)}px`}
      _focusVisible={{
        outline: '3px solid',
        outlineColor: 'accent',
        outlineOffset: '3px',
      }}
    >
      <img
        src={art.src}
        alt={label}
        width={layout.imageWidth}
        height={layout.imageHeight}
        style={{
          display: 'block',
          maxWidth: 'none',
          width: `${layout.imageWidth}px`,
          height: `${layout.imageHeight}px`,
          marginLeft: `${layout.offsetX}px`,
          marginTop: `${layout.offsetY}px`,
        }}
      />
    </Link>
  );
}

export function StoreBadges({
  links: given,
  device: givenDevice,
  height = 48,
  all = false,
  legal = false,
  apk = true,
}: {
  /** Defaults to the server's public config. */
  links?: StoreLinks;
  /** Defaults to this device (D345). */
  device?: StoreDevice;
  /** The visible badge height, never under Apple's 40px. */
  height?: number;
  /** Every configured link, whatever the device (Help). */
  all?: boolean;
  /** The trademark lines (where legal information is given). */
  legal?: boolean;
  /** Whether the direct APK may show (Android). */
  apk?: boolean;
}) {
  const fetched = useStoreLinks();
  const links = given ?? fetched;
  const language = useLanguage();
  const t = useT();
  const choice = all
    ? { ...links, worksBest: false }
    : storeChoice(links, givenDevice ?? storeDevice());
  const apkUrl = apk ? choice.apk : null;
  if (!hasAnyBadge({ ...choice, apk: apkUrl })) return null;

  const badgeHeight = Math.max(40, height);
  // Clear space: a quarter of the badge height around each one.
  const space = Math.ceil(badgeHeight / 4);

  return (
    <VStack
      align="flex-start"
      spacing={2}
      role="group"
      aria-label={t('site.stores.label')}
      data-testid="store-badges"
    >
      {(choice.appStore || choice.playStore) && (
        <Wrap spacing={`${space}px`} py={`${space / 2}px`}>
          {choice.appStore && (
            <WrapItem>
              <Badge
                href={choice.appStore}
                art={BADGES.appStore[language]}
                label={t('site.stores.appStoreAlt')}
                height={badgeHeight}
              />
            </WrapItem>
          )}
          {choice.playStore && (
            <WrapItem>
              <Badge
                href={choice.playStore}
                art={BADGES.playStore[language]}
                label={t('site.stores.playStoreAlt')}
                height={badgeHeight}
              />
            </WrapItem>
          )}
        </Wrap>
      )}
      {apkUrl && (
        <Box>
          <Button
            as="a"
            href={apkUrl}
            variant="outline"
            colorScheme="gray"
            leftIcon={<Download size={18} />}
            h="48px"
            px={5}
          >
            {t('site.stores.apk')}
          </Button>
          <Text fontSize="14px" color="muted" pt={2} maxW="420px">
            {t('site.stores.apkHint')}
          </Text>
        </Box>
      )}
      {choice.worksBest && (
        <Text fontSize="15px" color="muted">
          {t('site.stores.worksBest')}
        </Text>
      )}
      {legal && (choice.appStore || choice.playStore) && (
        <Text fontSize="12px" color="muted" maxW="680px" pt={1}>
          {[
            choice.appStore ? t('site.stores.legalApple') : null,
            choice.playStore ? t('site.stores.legalGoogle') : null,
          ]
            .filter(Boolean)
            .join(' ')}
        </Text>
      )}
    </VStack>
  );
}

/**
 * On the join pages (D346): a phone can install the app and open the link
 * again, since join links open in the app (D73, utils/appLinks.ts). Shown
 * only on a phone whose store (or APK) link is set.
 */
export function JoinInTheApp({ device: given }: { device?: StoreDevice }) {
  const links = useStoreLinks();
  const t = useT();
  const device = given ?? storeDevice();
  if (device === 'computer' || !hasAnyBadge(storeChoice(links, device))) {
    return null;
  }
  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="18px"
      px={4}
      py={3}
      mt={4}
      data-testid="join-in-the-app"
    >
      <Text fontWeight={700} fontSize="16px">
        {t('account.join.getApp')}
      </Text>
      <Text fontSize="15px" color="ink.body" lineHeight="21px" pt={1} pb={1}>
        {t('account.join.getAppBody')}
      </Text>
      <StoreBadges links={links} device={device} height={44} />
    </Box>
  );
}
